import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home, BookOpen, Route, User, Zap, Shield, LogOut,
  ChevronDown, Building, Users, Search, Menu, X,
  Award, Settings, Library, LayoutDashboard,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useSpace } from '../context/SpaceContext';
import CreditGauge from './CreditGauge';
import CmdK from './CmdK';
import Footer from './Footer';

const SPACE_ICONS = { famille: Users, organisation: Building, entreprise: Building };

// ─── SpaceSwitcher ────────────────────────────────────────────────────────────
function SpaceSwitcher({ compact = false }) {
  const { activeSpace, spaces, setActiveSpace } = useSpace();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (spaces.length === 0 && !activeSpace) return null;
  const ActiveIcon = activeSpace ? (SPACE_ICONS[activeSpace.type] || Building) : User;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="space-switcher-btn"
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors w-full"
      >
        <ActiveIcon size={12} className="text-[#94a3b8] shrink-0" />
        {!compact && (
          <span className="text-xs text-[#94a3b8] flex-1 text-left truncate">
            {activeSpace ? activeSpace.name : 'Personnel'}
          </span>
        )}
        <ChevronDown size={10} className={`text-[#94a3b8] transition-transform duration-200 shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          data-testid="space-switcher-dropdown"
          className="absolute left-0 top-full mt-2 w-56 bg-[#1a1927] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden z-[200]"
        >
          <div className="py-1">
            <button
              onClick={() => { setActiveSpace(null); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${!activeSpace ? 'text-[#ff6b6b]' : 'text-[#e8e8e8] hover:bg-white/5'}`}
              data-testid="space-option-personal"
            >
              <User size={13} className={!activeSpace ? 'text-[#ff6b6b]' : 'text-[#94a3b8]'} />
              <span className="flex-1">Espace personnel</span>
              {!activeSpace && <div className="w-1.5 h-1.5 rounded-full bg-[#ff6b6b]" />}
            </button>
            {spaces.length > 0 && <div className="border-t border-white/5 my-1" />}
            {spaces.map(space => {
              const Icon = SPACE_ICONS[space.type] || Building;
              const isActive = activeSpace?.id === space.id;
              return (
                <button
                  key={space.id}
                  onClick={() => { setActiveSpace(space); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${isActive ? 'text-[#ff6b6b]' : 'text-[#e8e8e8] hover:bg-white/5'}`}
                  data-testid={`space-option-${space.id}`}
                >
                  <Icon size={13} className={isActive ? 'text-[#ff6b6b]' : 'text-[#94a3b8]'} />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm">{space.name}</p>
                    <p className="text-[10px] text-[#94a3b8] capitalize">{space.type}</p>
                  </div>
                  {isActive && <div className="w-1.5 h-1.5 rounded-full bg-[#ff6b6b] shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sidebar Nav Items ────────────────────────────────────────────────────────
const SIDEBAR_NAV = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', key: 'dashboard' },
  { path: '/library',   icon: BookOpen, label: 'Mes Formations', key: 'formations' },
  { path: '/parcours', icon: Route, label: 'Parcours', key: 'parcours' },
  { path: '/profile', icon: Award, label: 'Compétences', key: 'competences' },
];

const BOTTOM_NAV = [
  { path: '/dashboard', icon: Home,    label: 'Accueil',  key: 'home' },
  { path: '/library',   icon: BookOpen, label: 'Contenus', key: 'contenus' },
  { path: '/parcours', icon: Route, label: 'Parcours', key: 'parcours' },
  { path: '/profile', icon: User, label: 'Profil', key: 'profile' },
  { path: '/credits', icon: Zap, label: 'Crédits', key: 'credits' },
];

// ─── User Dropdown (sidebar bottom) ──────────────────────────────────────────
function UserSection({ user, onLogout, collapsed }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initials = user?.name
    ? user.name.trim().split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const go = (path) => { navigate(path); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="user-menu-btn"
        className={`flex items-center gap-2.5 w-full px-2 py-2 rounded-xl hover:bg-[#20202a] transition-colors ${collapsed ? 'justify-center' : ''}`}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ff6b6b]/30 to-[#ffd93d]/30 border border-[#ff6b6b]/40 flex items-center justify-center shrink-0">
          <span className="font-bold text-xs text-white">{initials}</span>
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-[10px] text-[#64748b] truncate">{user?.email}</p>
          </div>
        )}
        {!collapsed && <ChevronDown size={13} className={`text-[#94a3b8] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>

      {open && (
        <div
          data-testid="user-dropdown"
          className="absolute bottom-full left-0 mb-2 w-52 bg-[#1a1927] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-[200]"
        >
          <div className="px-4 py-3 border-b border-white/5">
            <p className="text-white text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[#94a3b8] text-xs truncate">{user?.email}</p>
          </div>
          <div className="py-1">
            <button onClick={() => go('/profile')} data-testid="dropdown-profile"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/5 transition-colors text-left">
              <User size={14} className="text-[#94a3b8]" /> Mon profil
            </button>
            <button onClick={() => go('/settings')} data-testid="dropdown-settings"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/5 transition-colors text-left">
              <Settings size={14} className="text-[#94a3b8]" /> Paramètres
            </button>
            <button onClick={() => go('/credits')} data-testid="dropdown-credits"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/5 transition-colors text-left">
              <Zap size={14} className="text-[#ffd93d]" /> Mes crédits
            </button>
            {user?.role === 'admin' && (
              <button onClick={() => go('/superadmin')} data-testid="dropdown-superadmin"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#ffd93d] hover:bg-white/5 transition-colors text-left">
                <Shield size={14} className="text-[#ffd93d]" /> Super Admin
              </button>
            )}
          </div>
          <div className="border-t border-white/5 py-1">
            <button onClick={onLogout} data-testid="dropdown-logout"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#ff6b6b]/80 hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/5 transition-colors text-left">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Layout ──────────────────────────────────────────────────────────────
export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { loadSpaces } = useSpace();
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user) loadSpaces().catch(() => {});
  }, [user?.email]);

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    function handler(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdkOpen(o => !o);
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = useCallback(async () => {
    navigate('/');
    toast.success('À bientôt !');
    logout();
  }, [logout, navigate]);

  const isActive = (path, key) => {
    if (key === 'dashboard') return location.pathname === '/dashboard';
    if (key === 'formations') return location.pathname === '/library' || location.pathname.startsWith('/course') || location.pathname.startsWith('/generate');
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  if (!user) {
    // Unauthenticated — simple wrapper
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col">
        <header className="sticky top-0 z-50 bg-[#0f0e17]/90 backdrop-blur-xl border-b border-white/5 px-4 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/')} className="font-heading font-bold text-xl text-white">Kurso</button>
          </div>
        </header>
        <main className="flex-1">{children}</main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#0f0e17] overflow-hidden" data-testid="kurso-layout">
      {/* ── Sidebar (Desktop ≥lg: 280px | Tablet md-lg: 80px | Mobile: hidden) ── */}
      <aside
        className="hidden md:flex flex-col fixed left-0 top-0 h-full bg-[#16161e] border-r border-[#2a2935] z-40 transition-all duration-300 w-[80px] lg:w-[280px]"
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="px-4 pt-6 pb-4 border-b border-[#2a2935] flex items-center lg:gap-3 justify-center lg:justify-start">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="sidebar-logo"
            className="flex items-center gap-2"
          >
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#ff6b6b] to-[#ffd93d] flex items-center justify-center shrink-0">
              <span className="font-black text-[#0f0e17] text-sm">K</span>
            </div>
            <span className="font-heading font-black text-white text-lg hidden lg:block">Kurso</span>
          </button>
        </div>

        {/* Cmd+K Search */}
        <div className="px-3 py-4 border-b border-[#2a2935]">
          <button
            onClick={() => setCmdkOpen(true)}
            data-testid="sidebar-search-btn"
            className="w-full h-9 bg-[#0f0e17] border border-[#2a2935] rounded-lg flex items-center gap-2 px-2.5 text-[#64748b] text-sm hover:border-[#20c997] hover:text-white transition-colors"
          >
            <Search size={14} className="shrink-0" />
            <span className="hidden lg:block flex-1 text-left text-sm">Rechercher...</span>
            <kbd className="hidden lg:block text-[10px] bg-[#2a2935] px-1.5 py-0.5 rounded font-mono">⌘K</kbd>
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-1">
          {SIDEBAR_NAV.map(({ path, icon: Icon, label, key }) => {
            const active = isActive(path, key);
            return (
              <button
                key={key}
                onClick={() => navigate(path)}
                data-testid={`nav-${key}`}
                title={label}
                className={`flex items-center gap-3 px-2.5 py-2.5 rounded-xl w-full transition-all duration-200 justify-center lg:justify-start ${
                  active
                    ? 'bg-[#20c997]/10 text-[#20c997]'
                    : 'text-[#94a3b8] hover:bg-[#20202a] hover:text-white'
                }`}
              >
                <Icon size={18} strokeWidth={active ? 2.5 : 1.8} className="shrink-0" />
                <span className="hidden lg:block text-sm font-medium">{label}</span>
              </button>
            );
          })}

          {/* Space Switcher */}
          {(user) && (
            <div className="pt-4">
              <p className="hidden lg:block text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748b] px-2 mb-2">Espace</p>
              <div className="px-0.5">
                <SpaceSwitcher compact={false} />
              </div>
            </div>
          )}
        </nav>

        {/* Credits Gauge + User section */}
        <div className="px-3 pb-4 space-y-3 border-t border-[#2a2935] pt-3">
          <div className="hidden lg:block">
            <CreditGauge collapsed={false} />
          </div>
          <div className="block lg:hidden flex justify-center">
            <CreditGauge collapsed={true} />
          </div>
          {user && <UserSection user={user} onLogout={handleLogout} collapsed={false} />}
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main
        className="flex-1 md:ml-[80px] lg:ml-[280px] h-full overflow-y-auto pb-[72px] md:pb-0"
        data-testid="main-content"
      >
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 bg-[#0f0e17]/95 backdrop-blur-xl border-b border-[#2a2935] px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="font-heading font-black text-lg text-white">
            Kurso
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCmdkOpen(true)}
              data-testid="mobile-search-btn"
              className="p-2 rounded-lg bg-[#16161e] border border-[#2a2935] text-[#94a3b8] hover:text-white transition-colors"
            >
              <Search size={16} />
            </button>
            {user && (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ff6b6b]/30 to-[#ffd93d]/30 border border-[#ff6b6b]/40 flex items-center justify-center">
                <span className="font-bold text-xs text-white">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            )}
          </div>
        </header>

        <div className="min-h-full">
          {children}
          <Footer />
        </div>
      </main>

      {/* ── Bottom Nav (Mobile only) ── */}
      <nav
        className="md:hidden fixed bottom-0 left-0 w-full h-[72px] bg-[#16161e]/95 backdrop-blur-xl border-t border-[#2a2935] flex items-center justify-around px-1 z-50"
        data-testid="bottom-nav"
      >
        {BOTTOM_NAV.map(({ path, icon: Icon, label, key }) => {
          const active = isActive(path, key);
          return (
            <button
              key={key}
              onClick={() => navigate(path)}
              data-testid={`nav-${key}`}
              className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all duration-200 min-w-0 ${
                active ? 'text-[#20c997]' : 'text-[#94a3b8] hover:text-white'
              }`}
            >
              <Icon size={20} strokeWidth={active ? 2.5 : 1.5} />
              <span className="text-[10px] font-medium">{label}</span>
            </button>
          );
        })}
        {/* Logout */}
        <button
          onClick={handleLogout}
          data-testid="nav-logout"
          className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl text-[#94a3b8]/60 hover:text-[#ff6b6b] transition-colors"
        >
          <LogOut size={18} strokeWidth={1.5} />
          <span className="text-[10px] font-medium">Sortie</span>
        </button>
      </nav>

      {/* ── Cmd+K overlay ── */}
      {cmdkOpen && <CmdK onClose={() => setCmdkOpen(false)} />}
    </div>
  );
}
